import { PURPLE_700, RED_700 } from '@mnd-frontend/ui/shared/tokens';
import type { TFunction } from 'i18next';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AiNewsroomSettingsFragment,
  PressroomGoLiveFragment,
  UserBaseInfoFragment,
} from '../generated/types';
import type { NewsroomSetupSteps } from '../modules/newsroomSetup/types';
import { useQueryState } from './UseQueryStateContext';

export type NewsroomDataBranding = {
  primaryBrandColor: string;
  secondaryBrandColor: string;
  logoCloudinaryId?: string | null;
};

export type NewsroomDataPressContact = {
  id?: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  imageUrl?: string;
  imageResourceId?: string;
};

export type NewsroomDataCompanyDescription = {
  tagline: string;
  companyDescription: string;
};

export type NewsroomDataCompanyInformation = {
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

export type NewsroomData = {
  branding: NewsroomDataBranding;
  'press-contact': NewsroomDataPressContact;
  'company-description': NewsroomDataCompanyDescription;
  'company-information': NewsroomDataCompanyInformation;
};

const defaultNewsroomData = ({
  user,
  pressroom,
  aiGeneratedNewsroomSettings,
  t,
}: {
  user?: UserBaseInfoFragment;
  pressroom?: PressroomGoLiveFragment;
  aiGeneratedNewsroomSettings?: AiNewsroomSettingsFragment;
  t?: TFunction;
}): NewsroomData => {
  const newsroomSettings = pressroom?.newsroomSettings;
  const newsroomStyle = pressroom?.newsroomSettings?.newsroomStyle;
  const contact = pressroom?.contactPeopleList?.contactPeople2[0];

  return {
    branding: {
      primaryBrandColor: newsroomStyle?.primaryBrandColor ?? RED_700,
      secondaryBrandColor:
        newsroomStyle?.secondaryBrandColor ?? newsroomStyle?.primaryBrandColor ?? PURPLE_700,
      logoCloudinaryId: newsroomStyle?.logotype?.cloudinaryId,
    },
    'press-contact': {
      id: contact?.id,
      name: contact?.name ?? user?.name ?? '',
      title: contact?.title ?? (t && t('onboarding-press-contact_title')) ?? '',
      email: contact?.email ?? user?.email ?? '',
      phone: contact?.phone ?? user?.phone ?? '',
      imageUrl: contact?.imageUrl ?? '',
      imageResourceId: undefined,
    },
    'company-description': {
      tagline: newsroomSettings?.tagline ?? aiGeneratedNewsroomSettings?.tagline ?? '',
      companyDescription:
        newsroomSettings?.companyDescription ??
        aiGeneratedNewsroomSettings?.companyDescription ??
        '',
    },
    'company-information': {
      companyName: newsroomSettings?.companyName ?? '',
      street: newsroomSettings?.street ?? '',
      postalCode: newsroomSettings?.postalCode ?? '',
      city: newsroomSettings?.city ?? '',
      country: newsroomSettings?.country ?? '',
    },
  };
};

type NewsroomSetup = {
  newsroomData: NewsroomData;
  showReadyToGoLive: boolean;
  newsroomSetupStep: NewsroomSetupSteps;
  setNewsroomData: (data: NewsroomData) => void;
  handleNewsroomDataFieldChange: (group: string, key: string, value: string | null) => void;
  setNewsroomSetupStep: (step: NewsroomSetupSteps) => void;
};

const NewsroomSetupContext = createContext<NewsroomSetup>({
  newsroomData: defaultNewsroomData({}),
  showReadyToGoLive: false,
  newsroomSetupStep: undefined,
  setNewsroomData: () => null,
  handleNewsroomDataFieldChange: () => null,
  setNewsroomSetupStep: () => null,
});

type Props = {
  children: React.ReactNode;
  showReadyToGoLive: boolean;
  pressroom: PressroomGoLiveFragment;
  user: UserBaseInfoFragment;
  aiGeneratedNewsroomSettings?: AiNewsroomSettingsFragment;
};

export const NewsroomSetupContextProvider = ({
  children,
  showReadyToGoLive,
  pressroom,
  user,
  aiGeneratedNewsroomSettings,
}: Props) => {
  const { t } = useTranslation();
  const aiDataEffectRun = useRef(false);
  const [newsroomData, setNewsroomData] = useState(
    defaultNewsroomData({ user, pressroom, aiGeneratedNewsroomSettings, t }),
  );
  const [newsroomSetupStep, setNewsroomSetupStep] = useQueryState<NewsroomSetupSteps>(
    'onboarding-step',
    undefined,
  );

  const handleNewsroomDataFieldChange = useCallback(
    (group: string, key: string, value: string | null) => {
      setNewsroomData(prevNewsroomData => ({
        ...prevNewsroomData,
        [group as keyof NewsroomData]: {
          ...prevNewsroomData[group as keyof NewsroomData],
          [key]: value,
        },
      }));
    },
    [],
  );

  const values = useMemo(
    () => ({
      newsroomData,
      showReadyToGoLive,
      newsroomSetupStep,
      setNewsroomData,
      handleNewsroomDataFieldChange,
      setNewsroomSetupStep,
    }),
    [
      newsroomData,
      showReadyToGoLive,
      newsroomSetupStep,
      handleNewsroomDataFieldChange,
      setNewsroomSetupStep,
    ],
  );

  useEffect(() => {
    if (!aiGeneratedNewsroomSettings || aiDataEffectRun.current) return;
    const { tagline, companyDescription } = aiGeneratedNewsroomSettings;
    if (
      !newsroomData['company-description'].tagline &&
      !newsroomData['company-description'].companyDescription
    ) {
      setNewsroomData({
        ...newsroomData,
        'company-description': {
          tagline: tagline ?? '',
          companyDescription: companyDescription ?? '',
        },
      });
      aiDataEffectRun.current = true;
    }
  }, [aiGeneratedNewsroomSettings, newsroomData]);

  return <NewsroomSetupContext.Provider value={values}>{children}</NewsroomSetupContext.Provider>;
};

export const useNewsroomSetup = () => useContext(NewsroomSetupContext);
